import React from 'react';
import { FormGroup } from 'reactstrap';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import { FormCheckbox, Input } from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { LongTermStatus, LongTermStatusType } from './LongTermStatus';

export default class LongTermStatusForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            selectedLongTermStatus: props.selectedLongTermStatus ?? new LongTermStatus(),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { selectedLongTermStatus } = this.props;

        const response = await httpClient
            .delete(ApiRoutes.longTermStatus.delete(selectedLongTermStatus.id))
            .catch(this.handleSaveError);

        if (response) {
            this.props.onDeleteCallback(response);
        }
    };

    onSubmit = async () => {
        // Clear any fluent api errors
        this.clearErrors();
        await this.props.onSaveCallback();
        this.resetForm();
    };

    clearErrors = () => this.setState({ errors: {} });

    handleSaveError = (err) => handleFormSaveError(this, err);

    populateState = () => {
        this.setState({
            loading: false,
        });
    };

    resetForm = () => this.setState({ formValidated: false });

    render() {
        const { formValidated, validationMessage, loading } = this.state;
        const { selectedLongTermStatus, show, types } = this.props;

        if (!selectedLongTermStatus) {
            return '';
        }

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="longTermStatusForm"
                formIcon={faCalendarTimes}
                formTitle={
                    !!parseInt(selectedLongTermStatus.id ?? 0) > 0
                        ? 'Edit Long Term Status'
                        : 'Add Long Term Status'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                entityId={selectedLongTermStatus.id}
            >
                <Input
                    label="Name"
                    id="name"
                    name="selectedLongTermStatus.name"
                    value={selectedLongTermStatus.name ?? ''}
                    onChange={this.props.onChange}
                    placeholder="Enter Name (maximum 250 characters)"
                    maxLength="250"
                    pattern="[^><\][\\\x22,;|]+"
                    error="Name is required and can contain hyphens and periods."
                    required
                />

                <ValidatedSelect
                    id="selectedLongTermStatus.longTermStatusTypeId"
                    name="selectedLongTermStatus.longTermStatusTypeId"
                    label="Type"
                    required
                    options={types}
                    value={
                        types.find((s) => s.value === selectedLongTermStatus.longTermStatusTypeId)
                        ?? ''
                    }
                    onChange={this.props.onSelectChange}
                    error="A status type is required."
                    disabled={!!selectedLongTermStatus.id}
                />

                <FormGroup>
                    <FormCheckbox
                        id="showOnDispatch"
                        name="selectedLongTermStatus.hideFromDispatchBoard"
                        checked={
                            selectedLongTermStatus.hideFromDispatchBoard ?? ''
                        }
                        onChange={this.props.onChange}
                        labelText="Hide From Dispatch Board When Assigned"
                    />
                </FormGroup>

                {!selectedLongTermStatus.hideFromDispatchBoard && (
                    <FormGroup>
                        <FormCheckbox
                            id="allowDispatch"
                            name="selectedLongTermStatus.allowDispatch"
                            checked={selectedLongTermStatus.allowDispatch ?? ''}
                            onChange={this.props.onChange}
                            labelText="Allow Dispatch"
                        />
                    </FormGroup>
                )}

                <FormGroup>
                    <FormCheckbox
                        id="requiresEndDate"
                        name="selectedLongTermStatus.requiresEndDate"
                        checked={selectedLongTermStatus.requiresEndDate ?? ''}
                        onChange={this.props.onChange}
                        labelText="Requires End Date"
                    />
                </FormGroup>

                <FormGroup>
                    <FormCheckbox
                        id="showOnDispatchPlusHandoffEmail"
                        name="selectedLongTermStatus.showOnDispatchPlusHandoffEmail"
                        checked={
                            selectedLongTermStatus.showOnDispatchPlusHandoffEmail
                            ?? false
                        }
                        onChange={this.props.onChange}
                        labelText="Show on Dispatch+"
                    />
                </FormGroup>
                {selectedLongTermStatus.longTermStatusTypeId == LongTermStatusType.Employee && (
                    <FormGroup>
                        <FormCheckbox
                            id="isPTO"
                            name="selectedLongTermStatus.isPTO"
                            checked={selectedLongTermStatus.isPTO ?? ''}
                            onChange={this.props.onChange}
                            labelText="This is a PTO"
                        />
                    </FormGroup>
                )}
                {selectedLongTermStatus.showOnDispatchPlusHandoffEmail && (
                    <Input
                        label="Dispatch+ Label"
                        id="dispatchPlusLabel"
                        name="selectedLongTermStatus.dispatchPlusLabel"
                        value={selectedLongTermStatus.dispatchPlusLabel ?? ''}
                        onChange={this.props.onChange}
                        placeholder="Optional..."
                        maxLength="250"
                    />
                )}
                {selectedLongTermStatus.longTermStatusTypeId == LongTermStatusType.Equipment && (
                    <FormGroup>
                        <FormCheckbox
                            id="isFamLTS"
                            name="selectedLongTermStatus.isFamLTS"
                            checked={selectedLongTermStatus.isFamLTS ?? false}
                            onChange={this.props.onChange}
                            labelText="This is a FAM Status"
                        />
                    </FormGroup>
                )}
            </SlideForm>
        );
    }
}
